import type { MetaReducer } from '@ngrx/store';
import { NGRX_STORAGE_KEYS } from '../../../../core/store/ngrx-persisted-state.keys';
import { createStorageSyncMetaReducer } from '../../../../core/store/ngrx-storage-sync.meta-reducer';
import { UsersListFeatureState } from './users-list.models';
import { usersListFeature, initialUsersListState } from './users-list.reducer';
import { getPaginationPage } from './users-list.cache';

function shouldClearFromStorage(state: UsersListFeatureState): boolean {
  const filters = state.filters;
  const filtersDefault =
    (filters.name === null || filters.name === '') &&
    (filters.email === null || filters.email === '') &&
    (filters.phone === null || filters.phone === '') &&
    filters.roleId === null &&
    (filters.roleLabel === null || filters.roleLabel === '') &&
    filters.managerId === null &&
    (filters.managerLabel === null || filters.managerLabel === '') &&
    filters.joiningDate === null &&
    filters.status === true;
  const paginationDefault =
    state.pagination.firstRowOffset === 0 &&
    state.pagination.pageSize === initialUsersListState.pagination.pageSize &&
    getPaginationPage(state.pagination) === 1 &&
    state.pagination.totalRecords === 0;
  const sortDefault =
    state.sort.sortField === null &&
    state.sort.sortOrder === 1 &&
    state.sort.previousSortField === null &&
    state.sort.previousSortOrder === 0;
  const noGridData = state.gridData === null;
  return filtersDefault && paginationDefault && sortDefault && noGridData;
}

function parseState(raw: unknown): UsersListFeatureState | null {
  if (!raw || typeof raw !== 'object') return null;
  const partial = raw as Partial<UsersListFeatureState>;
  if (!partial.filters || !partial.pagination || !partial.sort) return null;

  const paginationPartial = partial.pagination as UsersListFeatureState['pagination'];
  const rawGridData = partial.gridData ?? null;
  const legacyTotalRecords =
    rawGridData && typeof rawGridData === 'object' && 'totalRecords' in rawGridData
      ? Number((rawGridData as { totalRecords?: number }).totalRecords) || 0
      : null;
  const normalizedGridData =
    rawGridData && typeof rawGridData === 'object' && Array.isArray(rawGridData.users)
      ? { users: rawGridData.users }
      : null;

  const parsedPagination: UsersListFeatureState['pagination'] = {
    ...initialUsersListState.pagination,
    ...paginationPartial,
    page: paginationPartial.page ?? getPaginationPage({
      ...initialUsersListState.pagination,
      ...paginationPartial
    }),
    totalRecords: paginationPartial.totalRecords ?? legacyTotalRecords ?? 0
  };

  return {
    filters: { ...initialUsersListState.filters, ...partial.filters },
    pagination: parsedPagination,
    sort: partial.sort,
    gridData: normalizedGridData
  };
}

export const usersListMetaReducers: MetaReducer[] = [
  createStorageSyncMetaReducer<UsersListFeatureState>({
    featureKey: usersListFeature.name,
    storageKey: NGRX_STORAGE_KEYS.usersList,
    shouldClearFromStorage,
    serialize: (state) => state,
    deserialize: parseState
  })
];
